import React, {useCallback, useEffect} from 'react';
import {callTargetedAction} from 'client/framework';
import type {QuestionModelV3} from '@albert-io/models';
import {useShallow} from 'zustand/react/shallow';
import {
  useQuestionTranslationStore,
  NoTranslationWarningHeader,
  btnDisabledWarning,
  translatedInstructions
} from 'components/QuestionTypes/shared/TranslationSection';
import {ALTERNATE_CONTENT_LANGUAGES} from 'components/QuestionTypes/shared/TranslationSection/Translation.types';
import AnnotatableContent from 'client/components/PracticeView/Annotations/AnnotatableContent';
import {buildQuestionRegionId} from 'client/components/PracticeView/Annotations/annotationRegions';

import textHighlightQuestionActions from '../TextHighlightQuestion.actions';

import TextHighlightPrompt from './TextHighlightPrompt.react';
import {useTranslatedHighlightPrompt} from './useTranslatedHighlightPrompt';

import '../text-highlight-question.scss';

interface TextHighlightInputAreaProps {
  isGuessCorrect?: boolean;
  isGuessSubmitted?: boolean;
  question?: QuestionModelV3;
  questionId?: string;
  storeName?: string;
  activeQuestionStore?: any;
}

/*

old component propTypes
  static propTypes = {
    isGuessCorrect: PropTypes.bool,
    isGuessSubmitted: PropTypes.bool,
    question: PropTypes.instanceOf(QuestionModelV3),
    questionId: PropTypes.string,
    storeName: PropTypes.string
  };
*/

const TextHighlightInputArea = ({
  storeName = 'textHighlightQuestionStore',
  question,
  questionId,
  isGuessSubmitted,
  isGuessCorrect,
  activeQuestionStore
}: TextHighlightInputAreaProps) => {
  const {showTranslations} = useQuestionTranslationStore(
    useShallow((state) => ({
      showTranslations: state.showTranslations && state.translationSettings.question
    }))
  );

  const store = activeQuestionStore ?? question?.getStore?.();
  const translatedPrompt = useTranslatedHighlightPrompt();

  const setUpQuestion = useCallback(
    (questionToSetup: QuestionModelV3) => {
      callTargetedAction({
        name: textHighlightQuestionActions.SET_UP_QUESTION,
        payload: {
          highlightPrompt: questionToSetup.getHighlightPrompt(),
          preferredPermutation: questionToSetup.getPreferredPermutation()
        },
        targetStore: storeName
      });
    },
    [storeName]
  );

  const clearSelections = useCallback(() => {
    callTargetedAction({
      name: textHighlightQuestionActions.CLEAR_SELECTIONS,
      targetStore: storeName
    });
  }, [storeName]);

  const shouldClearSelection = useCallback(() => {
    if (store.isGuessSubmitted === true && isGuessSubmitted === false) {
      clearSelections();
    }
  }, [store, isGuessSubmitted, clearSelections]);

  useEffect(() => {
    setUpQuestion(question);
    return () => {
      shouldClearSelection();
    };
  }, [storeName, question, shouldClearSelection, setUpQuestion]);

  return (
    <div className='text-highlight-question'>
      {showTranslations && (
        <NoTranslationWarningHeader className='mb-x4' text={btnDisabledWarning} />
      )}
      <h2>{showTranslations ? translatedInstructions.highlight : 'Highlight Answer(s) Below'}</h2>
      <AnnotatableContent
        regionId={questionId ? buildQuestionRegionId(questionId, 'text-highlight') : null}
        lang={translatedPrompt ? ALTERNATE_CONTENT_LANGUAGES[0] : null}
        className='contents'
      >
        <TextHighlightPrompt
          storeName={storeName}
          question={question}
          isGuessCorrect={isGuessCorrect}
          isGuessSubmitted={isGuessSubmitted}
          showTranslation={showTranslations}
          activeQuestionStore={activeQuestionStore}
        />
      </AnnotatableContent>
    </div>
  );
};

export default TextHighlightInputArea;
